/**
 * WorkReportFormModal — create/edit form for a Rapport (S3).
 *
 * Fields mirror the backend WorkReportCreate/Update schemas: Datum,
 * Von, Bis, Titel, Beschreibung, Typ, Mitarbeiter, optional linked
 * Aufgabe + entity links. Server-owned fields (`duration_minutes`,
 * `work_date`, `organization_id`, `created_by_user_id`) are NEVER part
 * of the payload — the duration shown here is a display-only preview.
 *
 * Times: the date + time inputs are LOCAL wall time and are converted
 * to UTC ISO via `dateTimeLocalToIso` (the established Vantio
 * convention — same as TaskFormModal's datetimeLocalToIso). Overnight
 * spans are rejected by the backend (422) and pre-flagged here.
 *
 * Theming: semantic tokens only (bg-card / text-foreground / border /
 * primary / destructive) so light AND dark mode work without a second
 * theme system. Uses ResponsiveModal → Radix Dialog ≥ md, Vaul bottom
 * sheet below (mobile-friendly per RESPONSIVE_PATTERNS.md).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../../components/ui/responsive-modal";
import TaskPicker from "./TaskPicker";
import MultiEntityLinkFields from "../../entityLinks/MultiEntityLinkFields";
import UserPicker from "../../users/UserPicker";
import UserMultiPicker from "../../users/UserMultiPicker";
import ActivityCategorySelect from "../../activityCategories/ActivityCategorySelect";
import { activityCategoryErrorMessage } from "../../activityCategories/activityCategoryErrors";
import { invalidateActivityCategoryCatalog } from "../../activityCategories/activityCategoryCatalogStore";
import {
  createAdminWorkReport,
  createAdminWorkReportBatch,
  updateAdminWorkReport,
} from "../../../../api/adminData";
import {
  CREATE_DEFAULT_KEYS,
  EMPTY_FORM,
  MAX_BATCH_PEOPLE,
  createDefaultLinks,
  formToBatchPayload,
  formToPayload,
  reportToForm,
  selectedUserIds,
  todayDateInputValue,
} from "./workReportFormState";
import {
  REPORT_TYPE_OPTIONS,
  dateTimeLocalToIso,
  formatMinutes,
  isoToDateInputValue,
  isoToTimeInputValue,
  previewDurationMinutes,
} from "./workReportConstants";

const fieldLabel =
  "mb-1 block text-[11px] font-medium text-muted-foreground";
const fieldInput =
  "w-full rounded-[8px] border border-border bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring";

// State shapes, prefill and payload builders live in
// ./workReportFormState (REPORT-PEOPLE, Regel 48). Re-exported so the
// existing test import keeps working unchanged.
export { formToPayload };

export default function WorkReportFormModal({
  open,
  mode, // "create" | "edit"
  report, // required for mode="edit"
  currentUserId,
  /**
   * C1.1 — links to prefill in CREATE mode, e.g. when the form is opened
   * from a task detail page. Supported keys are ``CREATE_DEFAULT_KEYS``
   * below: every link the form owns. Ignored in edit mode, where the
   * report itself is the source of truth. Absent/empty values simply
   * leave the picker unset — they never make it stop loading its normal
   * options.
   */
  createDefaults,
  onOpenChange,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // C3: the modal no longer bulk-loads ANY link options. All six links
  // (Aufgabe, Mietverhältnis, Mieter, Bewerbung, Einheit, Vermieter) are
  // async pickers that fetch one server page at a time, so opening the
  // form costs nothing until a picker is actually opened.

  // C1.1/C3: collapse the create-defaults into ONE primitive before the
  // effect sees them. Callers build the object inline, so its identity
  // changes on every parent render; as an effect dependency that would
  // re-run the reset below while the modal is open and wipe whatever the
  // user has typed. This string changes only when a value actually does.
  // ids are UUIDs, so "|" cannot occur inside one.
  const defaultsKey = CREATE_DEFAULT_KEYS.map((k) =>
    createDefaults?.[k] ? String(createDefaults[k]) : ""
  ).join("|");

  // Prefill / reset when the modal opens.
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && report) {
      // Edit wins outright: the stored report is the source of truth and
      // createDefaults are ignored entirely.
      setForm(reportToForm(report));
    } else {
      setForm({
        ...EMPTY_FORM,
        date: todayDateInputValue(),
        // REPORT-PEOPLE: the person capturing is the first person on the
        // list. Everyone else is a deliberate addition — nothing about
        // the task (neither its assignee nor its ASSIGN1 participants)
        // is read here. Being named on a task is not evidence of having
        // worked, and a prefilled name is a booking the moment someone
        // saves without re-reading the field.
        assigned_to_user_ids: currentUserId ? [String(currentUserId)] : [],
        // Opening from task B after task A must show B's links, so these
        // are part of the reset rather than a one-shot first-open prefill.
        ...createDefaultLinks(defaultsKey),
      });
    }
  }, [open, mode, report, currentUserId, defaultsKey]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  /** RESP2d: the category picker hands back a value, not an event —
   *  it owns its own `<select>` so both forms share one set of rules. */
  const setField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const startIso = dateTimeLocalToIso(form.date, form.startTime);
  const endIso = dateTimeLocalToIso(form.date, form.endTime);
  const durationPreview = previewDurationMinutes(startIso, endIso);

  const timeRangeInvalid =
    Boolean(startIso) && Boolean(endIso) && durationPreview === null;

  const isEdit = mode === "edit" && Boolean(report);
  // REPORT-PEOPLE: normalised the same way the server normalises, so the
  // preview counts exactly the bookings that will be created.
  const peopleIds = useMemo(() => selectedUserIds(form), [form]);
  const peopleCount = peopleIds.length;

  // The three numbers, kept apart on purpose. "Gesamtdauer" would be
  // ambiguous between the two of them, and that ambiguity is precisely
  // how three hours of work get read as one.
  const personMinutes =
    durationPreview !== null ? durationPreview * peopleCount : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Bitte eine Tätigkeit angeben.");
      return;
    }
    if (!form.date || !form.startTime || !form.endTime) {
      toast.error("Bitte Datum, Von und Bis angeben.");
      return;
    }
    if (timeRangeInvalid) {
      toast.error("»Bis« muss nach »Von« liegen.");
      return;
    }
    // Required for NEW reports only: an existing unclassified report must
    // stay saveable without forcing a guess.
    if (!isEdit && !form.activity_category) {
      toast.error("Tätigkeitsbereich ist erforderlich.");
      return;
    }
    if (!isEdit && peopleCount === 0) {
      toast.error("Bitte mindestens eine Person auswählen.");
      return;
    }
    if (!isEdit && peopleCount > MAX_BATCH_PEOPLE) {
      toast.error(
        `Höchstens ${MAX_BATCH_PEOPLE} Personen pro Erfassung.`
      );
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateAdminWorkReport(report.id, formToPayload(form));
        toast.success("Rapport aktualisiert.");
      } else if (peopleCount > 1) {
        // Several people → the atomic batch route. All rows or none.
        const result = await createAdminWorkReportBatch(
          formToBatchPayload(form)
        );
        const created = Number(result?.person_booking_count ?? peopleCount);
        const minutes = Number(
          result?.total_person_minutes ?? personMinutes ?? 0
        );
        toast.success(
          `${created} Personenbuchungen erstellt · ` +
            `${formatMinutes(minutes)} Personenzeit.`
        );
      } else {
        // Exactly one person → the unchanged single endpoint, so its
        // contract and every existing caller stay untouched.
        await createAdminWorkReport({
          ...formToPayload(form),
          assigned_to_user_id: peopleIds[0] || null,
        });
        toast.success("Rapport erfasst.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      // Backend 422s carry a clear German detail (time rules) or a
      // parsed pydantic message; 404 = cross-org/missing/inactive person.
      // The form stays OPEN so the input is not lost — and because the
      // write is atomic there is never a partial group to reconcile.
      // RESP2d: same treatment as the task form — refetch the catalog so
      // the retired option stops being offered, then say what to do.
      const categoryMessage = activityCategoryErrorMessage(err);
      if (categoryMessage) {
        void invalidateActivityCategoryCatalog();
        toast.error(categoryMessage);
      } else {
        toast.error(
          err?.message || "Rapport konnte nicht gespeichert werden."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // C3: every link is now an async picker. `setLink` is the single
  // onChange all six share — the payload shape is unchanged, each still
  // writes a bare id string (or "" to unlink).
  const setLink = useMemo(
    () => (key) => (id) => setForm((f) => ({ ...f, [key]: id })),
    []
  );

  const setIds = useCallback((field, next) => {
    setForm((f) => ({ ...f, [field]: next }));
  }, []);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
      title={mode === "edit" ? "Rapport bearbeiten" : "Rapport erfassen"}
      description="Arbeitszeit erfassen — die Dauer wird automatisch berechnet."
      contentClassName="max-w-[560px]"
    >
      {/* REPORT-UX1: three-zone layout — the FIELDS scroll, the button
          row stays reachable at every viewport height and zoom level.
          Structure mirrors TaskFormModal (flex column form, min-h-0
          scroll region, shrink-0 footer). ``pr-1`` keeps the scrollbar
          off the field edges. On mobile the drawer provides the scroll
          region instead and these classes are inert. */}
      <form
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col text-foreground"
      >
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="wr-date" className={fieldLabel}>
              Datum
            </label>
            <input
              id="wr-date"
              type="date"
              required
              className={fieldInput}
              value={form.date}
              onChange={set("date")}
            />
          </div>
          <div>
            <label htmlFor="wr-start" className={fieldLabel}>
              Von
            </label>
            <input
              id="wr-start"
              type="time"
              required
              className={fieldInput}
              value={form.startTime}
              onChange={set("startTime")}
            />
          </div>
          <div>
            <label htmlFor="wr-end" className={fieldLabel}>
              Bis
            </label>
            <input
              id="wr-end"
              type="time"
              required
              className={fieldInput}
              value={form.endTime}
              onChange={set("endTime")}
            />
          </div>
        </div>

        <div
          className="text-[12px] text-muted-foreground"
          data-testid="wr-duration-preview"
        >
          {/* REPORT-PEOPLE: "Einsatzdauer", not "Dauer" — how long the
              work took, as distinct from how much working time it cost.
              In edit mode a report is one person's booking and the two
              are the same number, so the old wording stays there. */}
          {isEdit ? "Dauer" : "Einsatzdauer"}:{" "}
          <span className="font-semibold text-amber-600 dark:text-amber-400">
            {durationPreview !== null ? formatMinutes(durationPreview) : "–"}
          </span>
          {timeRangeInvalid ? (
            <span className="ml-2 font-medium text-destructive">
              »Bis« muss nach »Von« liegen.
            </span>
          ) : null}
        </div>

        {/* REPORT-PEOPLE: the person-time preview. Deliberately NOT
            labelled "Gesamtdauer" — that word cannot distinguish the
            hour the shift lasted from the three hours of working time it
            produced, and the whole point of this feature is that the two
            stop being confused. Shown only while creating: an existing
            report is one person's booking.

            Nothing is shown when the time range is invalid — a computed
            "0 Personenminuten" next to a rejected span would read like a
            result rather than a missing input. */}
        {!isEdit ? (
          <div
            className="rounded-[8px] border border-border bg-muted/40 px-3 py-2 text-[12px] dark:border-[#1e2130] dark:bg-[#0d0f14]"
            data-testid="wr-person-time-preview"
          >
            <span className="font-semibold text-foreground">
              {peopleCount === 1
                ? "1 Personenbuchung"
                : `${peopleCount} Personenbuchungen`}
            </span>
            {personMinutes !== null && peopleCount > 0 ? (
              <>
                <span aria-hidden="true"> · </span>
                <span className="whitespace-nowrap font-semibold text-amber-600 dark:text-amber-400">
                  {formatMinutes(personMinutes)} Personenzeit
                </span>
                {peopleCount > 1 ? (
                  <span className="ml-1 text-muted-foreground">
                    ({peopleCount} × {formatMinutes(durationPreview)})
                  </span>
                ) : null}
              </>
            ) : null}
            {peopleCount > MAX_BATCH_PEOPLE ? (
              <p className="m-0 mt-1 font-medium text-destructive">
                Höchstens {MAX_BATCH_PEOPLE} Personen pro Erfassung.
              </p>
            ) : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="wr-title" className={fieldLabel}>
            Tätigkeit
          </label>
          <input
            id="wr-title"
            type="text"
            required
            maxLength={200}
            placeholder="Was wurde gemacht?"
            className={fieldInput}
            value={form.title}
            onChange={set("title")}
          />
        </div>

        <div>
          <label htmlFor="wr-description" className={fieldLabel}>
            Beschreibung (optional)
          </label>
          <textarea
            id="wr-description"
            rows={2}
            className={fieldInput}
            value={form.description}
            onChange={set("description")}
          />
        </div>

        <div>
          <label htmlFor="wr-activity-category" className={fieldLabel}>
            Tätigkeitsbereich{mode === "edit" ? " (optional)" : ""}
          </label>
          {/* RESP2d: the organisation's own areas, custom ones included.
              No people are touched here — a responsibility default is
              about who should DO work, and a report records who already
              did. */}
          <ActivityCategorySelect
            id="wr-activity-category"
            className={fieldInput}
            value={form.activity_category}
            onChange={(code) => setField("activity_category", code)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="wr-type" className={fieldLabel}>
              Typ
            </label>
            <select
              id="wr-type"
              className={fieldInput}
              value={form.report_type}
              onChange={set("report_type")}
            >
              {REPORT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {/* PICK1c: was a native <select> over the WHOLE org, handed
              down as the `users` prop — no search, a full user list per
              mounting page, and empty for a manager because
              /api/admin/users is admin-only and the 403 was swallowed.

              REPORT-PEOPLE: creating captures PEOPLE (plural), editing
              stays singular. That asymmetry is the design, not an
              oversight — a stored report IS one person's booking, and
              offering a multi-select there would ask what it means to
              add someone to a row that already has an assignee, a
              duration and possibly a cancellation. Corrections are made
              per person, so the edit form stays per person.

              The picker itself is ASSIGN1's, reused unchanged: same
              search, same paging, same chips, same server-side rule for
              who may be chosen (active users of this organisation, no
              role predicate). */}
          <div>
            {isEdit ? (
              /* The empty value still means "Ich selbst": the backend
                 falls back to the acting user when the field is blank
                 (work_report_service.py:256), so the clear row keeps
                 that wording rather than "niemand". */
              <UserPicker
                label="Mitarbeiter"
                value={form.assigned_to_user_id}
                onChange={(id) =>
                  setForm((f) => ({ ...f, assigned_to_user_id: id }))
                }
                placeholder="Ich selbst"
                clearLabel="Ich selbst"
              />
            ) : (
              <UserMultiPicker
                label="Mitarbeiter"
                value={form.assigned_to_user_ids}
                onChange={(ids) =>
                  setForm((f) => ({ ...f, assigned_to_user_ids: ids }))
                }
                maxSelections={MAX_BATCH_PEOPLE}
                placeholder="Person auswählen"
                searchPlaceholder="Person nach Name oder E-Mail suchen …"
                disabled={saving}
              />
            )}
          </div>
        </div>

        <details className="rounded-[8px] border border-border px-3 py-2">
          <summary className="cursor-pointer text-[12px] font-medium text-muted-foreground">
            Verknüpfungen (optional)
          </summary>
          {/* C1: searchable, server-paginated task link. */}
          <div className="mt-2">
            <TaskPicker
              value={form.task_id}
              onChange={setLink("task_id")}
              disabled={saving}
            />
          </div>
          {/* ME3a: the seven entity links, several per type. The D4b
              property/unit/room grouping went with the single-value
              pickers — with sets there is no "the" room to keep
              consistent with "the" unit. ``task_id`` above stays on its
              own because it is an owner relation, not an entity link. */}
          <div className="mt-3">
            <MultiEntityLinkFields
              value={form}
              onChange={setIds}
              row={mode === "edit" ? report : null}
              disabled={saving}
            />
          </div>
        </details>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border pt-3 mt-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving
              ? "Speichert …"
              : mode === "edit"
                ? "Speichern"
                : "Rapport erfassen"}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
